/**
 * Goudstaven API Service
 * Custom Influence.io Launcher API wrapper for Clean Nutrition loyalty program.
 * Replaces default widget with custom UI components.
 */
// Staat op de goudstaven-pagina in de header en in meerdere secties. Het
// eerste exemplaar blijft: een volgend exemplaar maakte de API opnieuw aan en
// gooide daarmee de lopende load() en de cache weg.
window.GoudstavenAPI = window.GoudstavenAPI || (function () {
  'use strict';

  const BASE_URL = 'https://launcher.api.influence.io/launcher';

  /**
   * Vertaalde beloningslabels. Dit bestand is een statisch asset en kan dus
   * niet zelf door de Liquid `t`-filter; snippets/goudstaven-i18n.liquid zet
   * de sleutels op window.GS_T voordat dit script iets rendert. De
   * Nederlandse tekst blijft als fallback staan, zodat een ontbrekende
   * sleutel nooit een leeg label oplevert.
   */
  function T(key, fallback) {
    const dict = (window.GS_T && window.GS_T.rewards) || {};
    return dict[key] || fallback;
  }

  function fill(template, values) {
    return window.GS_FILL
      ? window.GS_FILL(template, values)
      : Object.keys(values).reduce(function (out, k) {
          return out.split('[' + k + ']').join(values[k]);
        }, String(template));
  }
  let _config = null;
  let _customerCache = null;
  let _shopCache = {};
  let _loadPromise = null;

  // ========================================
  // INIT
  // ========================================

  function init() {
    const el = document.querySelector('.influenceio-widgets-init');
    if (!el) return null;

    _config = {
      shop: el.dataset.shop,
      customerId: el.dataset.customerId || null,
      customerEmail: el.dataset.customerEmail || null,
      digest: el.dataset.customerDigest || null,
    };

    return _config;
  }

  function getConfig() {
    if (!_config) init();
    return _config;
  }

  function isLoggedIn() {
    const cfg = getConfig();
    return cfg && cfg.customerId && cfg.digest;
  }

  // ========================================
  // HTTP
  // ========================================

  function buildParams(params) {
    const filtered = {};
    Object.keys(params).forEach(function (k) {
      if (params[k] != null && params[k] !== '') filtered[k] = params[k];
    });
    return new URLSearchParams(filtered).toString();
  }

  function get(path, params) {
    var qs = params ? buildParams(params) : '';
    var url = BASE_URL + path + (qs ? '?' + qs : '');

    // Geen content-type bij een GET: er gaat geen body mee, en met die header
    // deed de browser vóór elke aanvraag eerst een extra CORS-voorvraag
    // (preflight) naar Influence. Zonder is het een gewone aanvraag: per
    // pagina zo'n vijf rondjes naar Influence minder.
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  function post(path, body) {
    return fetch(BASE_URL + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  function put(path, body) {
    return fetch(BASE_URL + path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  // ========================================
  // AUTH PARAMS (reusable for customer endpoints)
  // ========================================

  function authParams(extra) {
    var cfg = getConfig();
    var base = {
      customer_id: cfg.customerId,
      customer_email: cfg.customerEmail,
      shop: cfg.shop,
      digest: cfg.digest,
    };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        base[k] = extra[k];
      });
    }
    return base;
  }

  // ========================================
  // CUSTOMER ENDPOINTS
  // ========================================

  /** Get full customer profile including pointBalance, tier, referralLink */
  function getCustomer(forceRefresh) {
    if (_customerCache && !forceRefresh) return Promise.resolve(_customerCache);
    if (!isLoggedIn()) return Promise.resolve(null);

    return get('/v1/customer', authParams()).then(function (data) {
      _customerCache = data;
      return data;
    });
  }

  /** Get points activity history */
  function getPointsHistory(limit, nextToken) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });

    var params = authParams({ limit: limit || 20 });
    if (nextToken) params.nextToken = nextToken;
    return get('/v1/customer/points', params);
  }

  /**
   * Hoeveel maanden een kortingscode bruikbaar blijft. De onderliggende
   * Shopify-korting verloopt na een jaar, maar Influence.io blijft de code
   * daarna gewoon teruggeven — de klant kopieert dan iets wat niet werkt.
   *
   * Te overschrijven met window.GS_REWARD_MONTHS (thema-instelling). 0 zet
   * het filter uit en toont alles, zoals voorheen.
   */
  var REWARD_MONTHS_DEFAULT = 12;

  function rewardWindowMonths() {
    var v = window.GS_REWARD_MONTHS;
    return typeof v === 'number' && v >= 0 ? v : REWARD_MONTHS_DEFAULT;
  }

  /**
   * Gooit codes weg die ouder zijn dan het venster.
   *
   * Het endpoint heet all-rewards en dat is letterlijk zo: er komt geen
   * status en geen vervaldatum op de code mee — de 'status' die je in de
   * respons ziet hoort bij de redeemRule, niet bij het exemplaar van deze
   * klant. createdAt is dus het enige signaal dat we hebben.
   *
   * Een item zonder bruikbare datum blijft staan. Liever een code te veel
   * dan er een weggooien die het nog doet.
   */
  function withinRewardWindow(items, months) {
    if (!months) return items;

    var cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);

    return items.filter(function (item) {
      if (!item || !item.createdAt) return true;
      var created = new Date(item.createdAt);
      if (isNaN(created.getTime())) return true;
      return created >= cutoff;
    });
  }

  /** Get all active (redeemable) rewards */
  function getActiveRewards(limit, type) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });

    var params = authParams({ limit: limit || 20 });
    if (type) params.type = type;
    return get('/v1/customer/all-rewards', params).then(function (data) {
      if (!data || !data.items) return data;
      return Object.assign({}, data, {
        items: withinRewardWindow(data.items, rewardWindowMonths()),
      });
    });
  }

  /** Get count of active rewards */
  function getActiveRewardsCount() {
    if (!isLoggedIn()) return Promise.resolve(0);
    return get('/v1/customer/all-rewards-count', authParams());
  }

  /** Redeem a reward - returns { success, couponCode } */
  function redeemReward(ruleId, points) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    var body = {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      ruleId: ruleId,
      redemptionSource: 'goudstaven-custom-widget',
    };
    if (points) body.points = points;

    return post('/v1/customer/redeem', body).then(function (data) {
      // Invalidate cache after redeem
      _customerCache = null;
      return data;
    });
  }

  /** Get referral activity */
  function getReferrals(limit) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });
    return get('/v1/customer/referrals', authParams({ limit: limit || 20 }));
  }

  /**
   * Meld een sociale missie (Instagram volgen en dergelijke) bij Influence.
   * Zo doet de eigen widget van Influence het ook: bij de klik gaat het
   * profiel open en gaat het id van de regel hierheen, en Influence schrijft
   * de goudstaven meteen bij. Er was eerst een veld 'action' in plaats van
   * 'ruleId', en niemand riep dit aan; de missie leverde dus nooit iets op.
   * Antwoord: { socialAction (bv. 'followInstagram'), updatedBalance,
   * newTierId, newTierProgress }.
   */
  function actionSocial(ruleId) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    return post('/v1/customer/social', {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      ruleId: ruleId,
    }).then(function (data) {
      _customerCache = null;
      return data;
    });
  }

  /**
   * Haal het klantobject vers op en meld het aan de pagina (event
   * goudstaven:customer), na iets wat het saldo veranderde. De pas in het
   * dashboard tekent zich daarop opnieuw.
   */
  function refreshCustomer() {
    return getCustomer(true).then(function (c) {
      if (c) emit('customer', { customer: c });
      return c;
    });
  }

  /** Update birthday */
  function updateBirthday(day, month) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    return put('/v1/customer/birthday', {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      birthdayDay: day,
      birthdayMonth: month,
    });
  }

  // ========================================
  // SHOP ENDPOINTS (no auth needed)
  // ========================================

  function shopParams() {
    return { shop: getConfig().shop };
  }

  /** Get all loyalty tiers with thresholds and perks */
  function getTiers() {
    if (_shopCache.tiers) return Promise.resolve(_shopCache.tiers);

    return get('/v1/shop/tiers', shopParams()).then(function (data) {
      _shopCache.tiers = data;
      return data;
    });
  }

  /**
   * Memberships staan los van de Loyalty Tiers. Gold Member+ is er zo een:
   * je koopt hem, je spaart hem niet, en hij komt dus niet in tiers.rules
   * voor. Dit is de enige bron voor dat niveau.
   */
  function getMemberships() {
    if (_shopCache.memberships) return Promise.resolve(_shopCache.memberships);

    return get('/v1/shop/memberships', shopParams()).then(function (data) {
      _shopCache.memberships = data;
      return data;
    });
  }

  /** Get single tier by ID */
  function getTier(tierId) {
    return get('/v1/shop/tiers/' + tierId, shopParams());
  }

  /** Get all earn rules */
  function getEarnRules() {
    if (_shopCache.earnRules) return Promise.resolve(_shopCache.earnRules);

    return get('/v1/shop/rules/earn', shopParams()).then(function (data) {
      _shopCache.earnRules = data;
      return data;
    });
  }

  /** Get all redeem rules */
  function getRedeemRules() {
    if (_shopCache.redeemRules) return Promise.resolve(_shopCache.redeemRules);

    return get('/v1/shop/rules/redeem', shopParams()).then(function (data) {
      _shopCache.redeemRules = data;
      return data;
    });
  }

  /** Get referral rules */
  function getReferralRules() {
    return get('/v1/shop/rules/referral', shopParams());
  }

  /** Get shop info */
  function getShop() {
    return get('/v1/shop', shopParams());
  }

  // ========================================
  // HELPERS
  // ========================================

  /** Format number with dots (NL style): 1500 -> "1.500" */
  function formatPoints(num) {
    if (num == null) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /**
   * Het niveau dat bij het huidige saldo hoort.
   *
   * Influence.io zet een tier alleen omhoog. `currentLoyaltyTierId` blijft op
   * Gold Member staan ook als de klant al zijn goudstaven uitgeeft; hun eigen
   * documentatie is daar stellig over ("tiers only go up") en de API heeft geen
   * veld om het terug te zetten. Bij ons kun je je niveau wél verliezen, dus
   * leiden we het af uit het saldo tegen dezelfde drempels die in Influence
   * staan. Zo blijft dat één bron: verandert de drempel daar, dan volgt de
   * site vanzelf.
   *
   * Gold Member+ hoort hier niet thuis. Dat niveau koop je en het staat los van
   * je saldo; dat komt in getDisplayTierTitle() samen.
   */
  function getEffectiveTier(payload) {
    if (!payload || !payload.customer || !payload.tiers || !payload.tiers.rules) return null;

    // Een handvol klanten staat rood, tot -14.358 toe, waarschijnlijk door
    // handmatige correcties. Zonder deze bodem haalt zo iemand zelfs de
    // drempel van 0 niet en komt hij op geen enkel niveau uit: dan verdwijnt
    // de niveaunaam van de pas en staat Team Goud op slot.
    var balance = Math.max(0, payload.customer.pointBalance || 0);
    var reached = null;

    payload.tiers.rules.forEach(function (tier) {
      var threshold = parseInt(tier.threshold, 10);
      if (isNaN(threshold) || balance < threshold) return;
      if (!reached || threshold > parseInt(reached.threshold, 10)) reached = tier;
    });

    return reached;
  }

  /**
   * Zet de afgeleide velden op een geladen payload: het effectieve niveau, de
   * voortgang, en de uitzondering voor Gold Member+.
   *
   * Staat apart van load() zodat een pagina dit na een inwissel opnieuw kan
   * draaien met een vers saldo, zonder die regels daar te herhalen.
   */
  function refreshDerived(payload) {
    if (!payload || !payload.customer || !payload.tiers) return payload;

    payload.effectiveTier = getEffectiveTier(payload);
    payload.tierProgress = getTierProgress(payload.customer, payload.tiers, payload.effectiveTier);

    // Met Gold Member+ ben je er al: dan geen "Nog 2.400 tot Gold Member" meer
    // onder de pas, ook al staat het saldo laag.
    if (payload.membership && payload.membership.goldMemberPlus && payload.tierProgress) {
      payload.tierProgress.nextTier = null;
      payload.tierProgress.pointsToNext = 0;
      payload.tierProgress.percentage = 100;
    }

    rememberPalette(payload);

    return payload;
  }

  /**
   * Onthoudt per klant het palet van de ledenkaart. snippets/member-card.liquid
   * zet het bij het volgende bezoek al vóór de eerste paint op de kaart, zodat
   * die niet eerst goud is en dan omspringt naar bijvoorbeeld de zwarte Team
   * Goud-kaart. Alleen de sleutel, geen saldo of andere klantdata.
   */
  function rememberPalette(payload) {
    var cfg = getConfig();
    if (!cfg || !cfg.customerId || !payload.customer) return;
    try {
      window.localStorage.setItem('cn-mcard-palette:' + cfg.customerId, getDisplayTierPalette(payload).key);
    } catch (e) {
      // Geen opslag (privévenster): dan gewoon zoals voorheen.
    }
  }

  /**
   * Determine tier progress as percentage (0-100).
   * Requires customer data and tiers data.
   *
   * Rekent op het saldo, niet op `currentLoyaltyTierProgress`: dat laatste is
   * bij Influence cumulatief en zakt nooit. Zie getEffectiveTier().
   */
  function getTierProgress(customer, tiers, effectiveTier) {
    if (!customer || !tiers || !tiers.rules) return null;

    var sorted = tiers.rules
      .slice()
      .sort(function (a, b) {
        return parseInt(a.threshold) - parseInt(b.threshold);
      });

    var currentTierId = effectiveTier ? effectiveTier.id : null;
    // Zelfde bodem als in getEffectiveTier(): een rood saldo is voor de
    // voortgangsbalk gewoon nul, anders wordt het percentage negatief.
    var progress = Math.max(0, customer.pointBalance || 0);

    var currentIdx = -1;
    sorted.forEach(function (t, i) {
      if (t.id === currentTierId) currentIdx = i;
    });

    // If at the highest tier
    if (currentIdx >= sorted.length - 1) {
      return { percentage: 100, currentTier: sorted[currentIdx], nextTier: null, pointsToNext: 0 };
    }

    var nextTier = sorted[currentIdx + 1];
    var currentThreshold = currentIdx >= 0 ? parseInt(sorted[currentIdx].threshold) : 0;
    var nextThreshold = parseInt(nextTier.threshold);
    var range = nextThreshold - currentThreshold;
    var progressInRange = progress - currentThreshold;
    var pct = range > 0 ? Math.min(100, Math.max(0, Math.round((progressInRange / range) * 100))) : 0;

    return {
      percentage: pct,
      currentTier: currentIdx >= 0 ? sorted[currentIdx] : null,
      nextTier: nextTier,
      pointsToNext: Math.max(0, nextThreshold - progress),
    };
  }

  /**
   * Beschrijf een redeem rule of geclaimde reward voor weergave.
   * Redeem rules (shop-endpoint) hebben templateName + product;
   * geclaimde rewards (customer/all-rewards) hebben rewardType.
   * Giftcards leveren een tegoedbon op; productdiscount-rewards (zoals
   * Training in de Gouden Tempel) leveren een kortingscode op waarmee je
   * het gekoppelde product (deels) gratis bestelt.
   */
  function getRewardMeta(rule) {
    var type = (rule.templateName || rule.rewardType || '').toLowerCase();
    var product = rule.product || null;
    var meta = {
      isGiftcard: type.indexOf('giftcard') !== -1,
      isFreeProduct: false,
      badge: null,
      note: null,
      imageUrl: null,
      productUrl: null,
      inStock: true,
    };

    if (meta.isGiftcard) {
      meta.badge = T('giftcardBadge', 'Tegoedbon');
      meta.note = T('giftcardNote', 'Je ontvangt een kortingscode ter waarde van dit bedrag.');
      return meta;
    }

    if (type.indexOf('productdiscount') !== -1) {
      if (product) {
        meta.imageUrl = product.imageUrl || null;
        meta.productUrl = product.externalUrlHandle ? '/products/' + encodeURIComponent(product.externalUrlHandle) : null;
        meta.inStock = product.inStock !== false;
      }

      if (!rule.discountValue || parseInt(rule.discountValue) >= 100) {
        meta.isFreeProduct = true;
        meta.badge = T('freeProduct', 'Gratis product');
        meta.note = T('freeProductNote', 'Je ontvangt een kortingscode waarmee je dit product gratis bestelt.');
      } else {
        meta.badge = fill(T('percentOff', '[value]% korting'), { value: rule.discountValue });
        meta.note = fill(T('percentOffNote', 'Je ontvangt een kortingscode voor [value]% korting op dit product.'), { value: rule.discountValue });
      }
      return meta;
    }

    if (type.indexOf('freeshipping') !== -1) {
      meta.badge = T('freeShipping', 'Gratis verzending');
    } else if (type.indexOf('percentage') !== -1) {
      meta.badge = rule.discountValue
        ? fill(T('percentOff', '[value]% korting'), { value: rule.discountValue })
        : T('discount', 'Korting');
    } else if (type.indexOf('amountdiscount') !== -1) {
      meta.badge = rule.discountValue
        ? fill(T('amountOff', '€[value] korting'), { value: rule.discountValue })
        : T('discount', 'Korting');
    }

    return meta;
  }

  /**
   * Bepaalt of een redeem rule nog daadwerkelijk verzilverd kan worden.
   * Influence.io laat rewards soms staan als status "Active" terwijl het
   * gekoppelde product in Shopify is verwijderd of gearchiveerd - dan is
   * `product` afwezig of heeft het een status != "active". Zulke rewards
   * kun je niet claimen, dus filteren we ze weg (spiegelt isRuleActive in
   * de missions-sectie). Giftcards hebben geen product en blijven zichtbaar
   * zolang de rule Active is. Een product dat nog bestaat maar tijdelijk
   * uitverkocht is (inStock === false) blijft wel zichtbaar maar disabled -
   * dat regelt getRewardMeta().inStock in de render.
   */
  function isRewardRedeemable(rule) {
    if (!rule) return false;
    if (rule.status && String(rule.status).toLowerCase() !== 'active') return false;

    var type = (rule.templateName || '').toLowerCase();
    if (type.indexOf('productdiscount') !== -1) {
      var product = rule.product;
      if (!product) return false; // product verwijderd
      if (product.status && String(product.status).toLowerCase() !== 'active') return false; // gearchiveerd / concept
    }
    return true;
  }

  /**
   * Get tier-specific gradient colors for membership cards.
   * Returns { key, gradient, accent, text, badge } per tier title.
   *
   * De namen komen uit Influence.io en worden hier op het woord herkend. De
   * oude reeks (Brons / Zilver / Goud) en de nieuwe (Team Goud / Gold Member /
   * Gold Member+) staan er allebei in, zodat het hernoemen in Influence.io geen
   * moment oplevert waarop de kaarten de verkeerde kleur hebben. De oude drie
   * mogen eruit zodra die tiers daar niet meer bestaan.
   *
   * De volgorde is hier de logica, niet decoratie — de eerste treffer wint:
   *
   *   - 'gold member+' staat voor 'gold member', want de plus-naam bevat de
   *     andere volledig.
   *   - 'team goud' staat voor 'goud', anders krijgt het instapniveau de
   *     gouden kaart in plaats van de zwarte.
   *
   * Dat is precies waar de oude opzet op stukliep: die liep alle sleutels langs
   * en hield de laatste treffer over, dus 'Team Goud' werd goud en 'Gold
   * Member' viel door de mand naar de donkere fallback.
   */
  var TIER_PALETTES = [
    // --- Nieuwe reeks ---
    {
      match: ['gold member+', 'gold member plus', 'goldmember+'],
      key: 'gold-member-plus',
      // De kaart wordt in de CSS gezet (een geborstelde SVG-achtergrond), dus
      // hier geen inline verloop overheen. Zie css-vlag hieronder.
      css: true,
      // Gepolijst edelmetaal. De eerste versie was champagne en las als crème:
      // lichter dan Gold Member, en daarmee juist mínder in plaats van meer.
      // Nu donkere hoeken met scherpe lichtbanen ertussen — dat leest als
      // massief metaal met glans, niet als een bleke variant van goud.
      //
      // De donkerste stop (#a07b3c) is bewust niet dieper: daaronder haalt de
      // donkere tekst geen contrast meer op de hoeken van de kaart.
      gradient:
        'linear-gradient(135deg, #a07b3c 0%, #f0dcac 12%, #c9a865 26%, #fdf7e6 42%, #d4b573 56%, #f3e3ba 70%, #b08c48 86%, #8f6d33 100%)',
      accent: '#c8951a',
      text: '#1a1a1a',
      badge: '#eadcb4',
    },
    {
      match: ['team goud', 'team gold'],
      key: 'team-goud',
      // Zwarte kaart met goud als accent; het instapniveau moet zich juist
      // onderscheiden van de twee gouden kaarten erboven.
      gradient: 'linear-gradient(135deg, #2e2e2e 0%, #000 100%)',
      accent: '#d4a437',
      text: '#fff',
      badge: '#1a1a1a',
    },
    {
      match: ['gold member', 'goldmember'],
      key: 'gold-member',
      // Merkverloop --gold-gradient staat in de CSS; geen inline achtergrond.
      css: true,
      gradient: 'linear-gradient(135deg, #f2c94c 0%, #c8951a 100%)',
      accent: '#f5d76e',
      text: '#1a1a1a',
      badge: '#f2c94c',
    },

    // --- Huidige reeks ---
    {
      match: ['diamant'],
      key: 'diamant',
      gradient: 'linear-gradient(135deg, #b9f2ff 0%, #4fc3f7 50%, #0288d1 100%)',
      accent: '#81d4fa',
      text: '#0a1628',
      badge: '#4fc3f7',
    },
    {
      match: ['zilver'],
      key: 'zilver',
      gradient: 'linear-gradient(135deg, #c0c0c0 0%, #808080 100%)',
      accent: '#d4d4d4',
      text: '#fff',
      badge: '#c0c0c0',
    },
    {
      match: ['brons'],
      key: 'brons',
      gradient: 'linear-gradient(135deg, #ba784d 0%, #8b4622 100%)',
      accent: '#d4915f',
      text: '#fff',
      badge: '#cd7f32',
    },
    // Vangnet voor het kale 'Goud' van nu. Staat onderaan, zodat 'Team Goud'
    // er nooit aan toekomt.
    {
      match: ['goud', 'gold'],
      key: 'gold-member',
      gradient: 'linear-gradient(135deg, #f2c94c 0%, #c8951a 100%)',
      accent: '#f5d76e',
      text: '#1a1a1a',
      badge: '#f2c94c',
    },
  ];

  var TIER_FALLBACK = {
    key: 'onbekend',
    gradient: 'linear-gradient(135deg, #333 0%, #111 100%)',
    accent: '#555',
    text: '#fff',
    badge: '#444',
  };

  /**
   * De titel die als niveau getoond moet worden.
   *
   * Gold Member+ is in Influence.io een Membership en geen Loyalty Tier: de
   * tier blijft daar 'Gold Member' staan. Voor de klant is het wel gewoon zijn
   * niveau, dus hier komen die twee bronnen samen. Dit is bewust de enige plek
   * waar dat gebeurt — de kaart, de drawer en de tier-rij lezen allemaal deze
   * functie, zodat ze niet uit elkaar kunnen lopen.
   */
  function getDisplayTierTitle(payload) {
    if (payload && payload.membership && payload.membership.goldMemberPlus) {
      return tierLabel(payload.membership.plus) || 'Gold Member+';
    }
    if (payload && payload.effectiveTier) {
      return tierLabel(payload.effectiveTier);
    }
    if (payload && payload.tierProgress && payload.tierProgress.currentTier) {
      return tierLabel(payload.tierProgress.currentTier);
    }
    return null;
  }

  /**
   * De naam van een niveau zoals wij hem tonen, niet zoals Influence hem noemt.
   *
   * snippets/goudstaven-i18n.liquid vertaalt tiers op UUID, want de titel in de
   * Influence-admin is werktekst die daar gerust mag veranderen. Dat is nu ook
   * de bedoeling: de tier daar gaat 'ooit 2.500 behaald' heten, omdat Influence
   * alleen het hoogste ooit bijhoudt en niet je huidige niveau.
   *
   * Alles wat een niveaunaam toont of er een kleur bij zoekt moet dus hierlangs.
   * Anders komt die werktekst op de pas terecht, of valt het kleurenpalet terug
   * op grijs omdat het de woorden 'gold member' niet meer herkent.
   */
  function tierLabel(tier) {
    if (!tier) return null;
    if (window.GS_INF) return window.GS_INF(tier.id, tier.title);
    return tier.title;
  }

  /**
   * Vult de voortgangsregel ("Nog [points] tot [tier]") en zet het
   * goudstaaf-icoon vlak vóór het aantal.
   *
   * Bouwt met DOM-knopen in plaats van innerHTML: de tiernaam komt uit de
   * API en hoeft dan niet ge-escaped te worden.
   *
   * De icoon-URL komt uit de kaart zelf — daar staat het beeld al. Zo hoeft
   * de asset-URL niet in twee Liquid-bestanden herhaald te worden.
   */
  function setProgressSub(subEl, cardEl, template, pointsText, tierText) {
    if (!subEl) return;

    var text = String(template || 'Nog [points] tot [tier]').split('[tier]').join(tierText || '');
    var parts = text.split('[points]');

    subEl.textContent = '';
    subEl.appendChild(document.createTextNode(parts[0]));

    var source = cardEl && cardEl.querySelector('.cn-mcard__points-icon');
    if (source && source.getAttribute('src')) {
      var icon = document.createElement('img');
      icon.className = 'cn-mcard__sub-icon';
      icon.src = source.getAttribute('src');
      icon.alt = '';
      subEl.appendChild(icon);
    }

    subEl.appendChild(document.createTextNode(pointsText + (parts.slice(1).join('[points]') || '')));
  }

  /**
   * Het kleurpalet van een niveau, opgezocht op UUID.
   *
   * getTierColors() matcht op woorden in de titel, en die titel is niet
   * betrouwbaar: in de Influence-admin is het werktekst (deze tier gaat 'ooit
   * 2.500 behaald' heten) en op de site is hij vertaald. Beide laten het palet
   * terugvallen op grijs. Het UUID verandert nooit, dus dat is de haak.
   *
   * De tabel staat in snippets/goudstaven-i18n.liquid, bij de vertalingen op
   * datzelfde UUID, zodat er één plek is waar die id's genoteerd staan.
   */
  function getTierPalette(tier) {
    var map = window.GS_TIER_PALETTES || {};
    var key = tier && tier.id ? map[tier.id] : null;

    if (key) {
      for (var i = 0; i < TIER_PALETTES.length; i++) {
        if (TIER_PALETTES[i].key === key) return TIER_PALETTES[i];
      }
    }

    // Onbekend niveau: terugvallen op de onvertaalde titel, zoals het altijd
    // ging. Een nieuwe tier in Influence krijgt zo nog steeds kleur.
    return getTierColors(tier && tier.title);
  }

  /**
   * Het palet van het niveau dat de klant nú heeft. Zelfde volgorde als
   * getDisplayTierTitle(), zodat naam en kleur niet uit elkaar kunnen lopen.
   */
  function getDisplayTierPalette(payload) {
    if (payload && payload.membership && payload.membership.goldMemberPlus) {
      return getTierPalette(payload.membership.plus);
    }
    if (payload && payload.effectiveTier) {
      return getTierPalette(payload.effectiveTier);
    }
    if (payload && payload.tierProgress && payload.tierProgress.currentTier) {
      return getTierPalette(payload.tierProgress.currentTier);
    }
    return TIER_FALLBACK;
  }

  function getTierColors(tierTitle) {
    var title = (tierTitle || '').toLowerCase();

    for (var i = 0; i < TIER_PALETTES.length; i++) {
      var palette = TIER_PALETTES[i];
      for (var j = 0; j < palette.match.length; j++) {
        if (title.indexOf(palette.match[j]) !== -1) return palette;
      }
    }

    return TIER_FALLBACK;
  }

  // ========================================
  // EVENTS
  // ========================================

  function emit(eventName, detail) {
    document.dispatchEvent(new CustomEvent('goudstaven:' + eventName, { detail: detail }));
  }

  /**
   * Initialize and load all data, then fire events.
   * Call this on DOMContentLoaded. Idempotent: meerdere secties op een
   * pagina mogen dit veilig aanroepen, er wordt maar 1x geladen.
   */
  function load() {
    if (_loadPromise) return _loadPromise;

    var cfg = init();
    if (!cfg) return Promise.resolve();

    var promises = [getTiers(), getEarnRules(), getRedeemRules(), getReferralRules(), getMemberships()];
    if (isLoggedIn()) {
      promises.push(getCustomer());
    }

    _loadPromise = Promise.all(promises).then(function (results) {
      var payload = {
        tiers: results[0],
        earnRules: results[1],
        redeemRules: results[2],
        referralRules: results[3],
        memberships: results[4],
        customer: results[5] || null,
        isLoggedIn: isLoggedIn(),
      };

      // Gold Member+ komt uit de memberships en niet uit de tiers. Of de klant
      // hem heeft staat op currentMembershipId in het klantobject.
      //
      // Zoeken op UUID en niet op de eerste uit de lijst. Er staan er nu twee:
      // het gekochte Gold Member+ en het Gold Member dat de loyalty-worker
      // toekent op saldo. Op volgorde vertrouwen zou betekenen dat elke
      // Gold Member als Gold Member+ getoond wordt zodra Influence die lijst
      // anders teruggeeft, en daar zitten inmiddels duizenden klanten in.
      var lijst = (payload.memberships && payload.memberships.memberships) || [];
      var paletten = window.GS_TIER_PALETTES || {};
      var plus = null;
      for (var i = 0; i < lijst.length; i++) {
        if (paletten[lijst[i].id] === 'gold-member-plus') { plus = lijst[i]; break; }
      }
      // Onbekend UUID (nieuwe winkel, hernoemde groep): dan maar de eerste,
      // zoals het hiervoor ook ging.
      if (!plus && lijst.length) plus = lijst[0];
      if (plus) {
        payload.membership = {
          plus: plus,
          goldMemberPlus: !!(payload.customer && payload.customer.currentMembershipId === plus.id),
        };
      }

      refreshDerived(payload);

      emit('loaded', payload);
      return payload;
    }).catch(function (err) {
      console.error('[GoudstavenAPI]', err);
      emit('error', { error: err });
      _loadPromise = null;
    });

    return _loadPromise;
  }

  // ========================================
  // WINKELWAGEN
  // ========================================

  /**
   * Zet het product van een beloning met de kortingscode in de winkelwagen.
   * Gedeeld door /pages/goudstaven en de account-drawer, zodat de knop op
   * beide plekken precies hetzelfde doet.
   *
   * Eerst de code, dan het product: /discount/CODE met /cart.js als antwoord
   * (zelfde route als de welkomstcode) zet de code op de winkelwagen, en het
   * product komt er daarna meteen met korting in. Andersom stond het even voor
   * de volle prijs in de winkelwagen, en daar reageren de cadeaus op die bij
   * een bedrag horen. Lukt het zetten van de code niet, dan gaat het product
   * er toch in; de code werkt dan nog bij het afrekenen.
   *
   * Opent zelf geen lade of pagina; dat doet de aanroeper.
   */
  function addRewardToCart(productUrl, couponCode) {
    // Alleen een productpad van deze winkel: de waarde komt uit Influence of
    // uit een data-attribuut, en '//ergens.anders' zou een fetch naar een
    // ander domein worden.
    if (!/^\/products\/[^/?#]+$/.test(String(productUrl || '')) || !couponCode) {
      return Promise.reject(new Error('GoudstavenAPI: invalid reward'));
    }

    var root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    var variantId = null;

    return fetch(root + productUrl + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('GoudstavenAPI: product fetch failed');
        return r.json();
      })
      .then(function (product) {
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { variant = product.variants[i]; break; }
        }
        if (!variant) variant = product.variants[0];
        if (!variant) throw new Error('GoudstavenAPI: no variant');
        variantId = variant.id;

        return fetch(root + '/discount/' + encodeURIComponent(couponCode) + '?redirect=' + encodeURIComponent(root + '/cart.js'), { credentials: 'same-origin' })
          .catch(function () {});
      })
      .then(function () {
        return fetch(root + '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('GoudstavenAPI: cart add failed');
        return true;
      });
  }

  // ========================================
  // PUBLIC API
  // ========================================

  return {
    init: init,
    load: load,
    isLoggedIn: isLoggedIn,

    // Customer
    getCustomer: getCustomer,
    getPointsHistory: getPointsHistory,
    getActiveRewards: getActiveRewards,
    getActiveRewardsCount: getActiveRewardsCount,
    redeemReward: redeemReward,
    getReferrals: getReferrals,
    actionSocial: actionSocial,
    refreshCustomer: refreshCustomer,
    updateBirthday: updateBirthday,

    // Shop
    getTiers: getTiers,
    getTier: getTier,
    getEarnRules: getEarnRules,
    getRedeemRules: getRedeemRules,
    getReferralRules: getReferralRules,
    getShop: getShop,

    // Helpers
    formatPoints: formatPoints,
    tierLabel: tierLabel,
    getTierPalette: getTierPalette,
    getDisplayTierPalette: getDisplayTierPalette,
    getEffectiveTier: getEffectiveTier,
    refreshDerived: refreshDerived,
    getTierProgress: getTierProgress,
    getTierColors: getTierColors,
    getDisplayTierTitle: getDisplayTierTitle,
    getMemberships: getMemberships,
    setProgressSub: setProgressSub,
    getRewardMeta: getRewardMeta,
    isRewardRedeemable: isRewardRedeemable,

    // Winkelwagen
    addRewardToCart: addRewardToCart,
  };
})();
